<script setup lang="ts">
import type { MaybeRefOrGetter } from "vue"
import type { RouteLocationRaw } from "vue-router"
import type { IconKeys } from "../Icon/Icon.interface"
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuRoot, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "reka-ui"
import { computed, toValue } from "vue"
import { AppLink, Icon } from "@/components/ui"

interface IDropdownMenuItem {
  key: string
  label: string
  onClick?: () => void
  href?: string
  to?: string | RouteLocationRaw
  variant?: "primary" | "danger" | "highlighted"
  dismissOnInteract?: boolean
  isHidden?: MaybeRefOrGetter<boolean>
  disabled?: MaybeRefOrGetter<boolean>
  children?: IDropdownMenuItem[]
  icon?: IconKeys
}

interface DropdownMenuProps {
  modal?: boolean
  items?: IDropdownMenuItem[]
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "end"
  disableTeleport?: boolean
  disabled?: boolean
  triggerClass?: string
}

const props = withDefaults(defineProps<DropdownMenuProps>(), {
  modal: false,
  items: () => [],
  side: "bottom",
  align: "start",
  disableTeleport: false,
  disabled: false,
  triggerClass: "flex w-max",
})

const open = defineModel<boolean>("open", { default: false })

function toggle() {
  if (props.disabled)
    return
  open.value = !open.value
}

defineExpose({ toggle })

function onItemClick(item: IDropdownMenuItem) {
  if (toValue(item.disabled))
    return
  if (item.onClick)
    item.onClick()

  if (item.dismissOnInteract)
    open.value = false
}

function componentType(item: IDropdownMenuItem) {
  return item.to ? AppLink : item.href ? "a" : "button"
}

const isOpen = computed(() => open.value)

function getVisibleChildren(children?: IDropdownMenuItem[]) {
  if (!children)
    return []
  return children.filter(child => !toValue(child.isHidden))
}
</script>

<template>
  <DropdownMenuRoot v-model:open="open" :disabled="props.disabled" :modal="modal">
    <DropdownMenuTrigger as-child :class="triggerClass" :disabled="props.disabled">
      <slot name="activator" v-bind="{ toggle, isOpen }" />
    </DropdownMenuTrigger>

    <DropdownMenuPortal :disabled="disableTeleport">
      <DropdownMenuContent
        class="bg-white flex flex-col p-2 shadow-sm gap-2 rounded-xl z-50 min-h-0 max-h-(--reka-dropdown-menu-content-available-height) overflow-y-auto overscroll-contain"
        :side="side"
        :align="align"
        avoid-collisions
        :collision-padding="12"
        sticky="partial"
        data-test="dropdown-menu"
      >
        <slot v-bind="{ toggle, isOpen }">
          <template v-for="(item, index) in items" :key="`dropdown-menu-item-${index}`">
            <template v-if="!toValue(item.isHidden)">
              <DropdownMenuSub v-if="item.children?.length">
                <DropdownMenuSubTrigger
                  as-child
                  class="dropdown-menu-item"
                  :class="[
                    item.variant ? `dropdown-menu-item_${item.variant}` : '',
                    toValue(item.disabled) && 'dropdown-menu-item_disabled',
                  ]"
                  :disabled="toValue(item.disabled)"
                >
                  <component
                    :is="componentType(item)"
                    :href="item.href"
                    :to="item.to"
                    :data-test="`dropdown-menu-item-${item.key}`"
                    class="flex items-center text-left gap-2 w-full"
                    :class="toValue(item.disabled) && 'dropdown-menu-item_disabled'"
                    @click="(e: Event) => { if (toValue(item.disabled)) e.preventDefault(); onItemClick(item); }"
                  >
                    <Icon v-if="item.icon" :icon="item.icon" class="p-0 -ml-[5px]" size="sm" />
                    {{ item.label }}
                  </component>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent
                    class="bg-white flex flex-col p-2 shadow-sm gap-2 rounded-xl z-50 min-h-0 max-h-(--reka-dropdown-menu-content-available-height) overflow-y-auto overscroll-contain"
                    avoid-collisions
                    :collision-padding="12"
                    sticky="partial"
                  >
                    <template v-for="child in getVisibleChildren(item.children)" :key="`dropdown-menu-item-${item.key}-${child.key}`">
                      <DropdownMenuItem
                        as-child
                        :class="[
                          child.variant ? `dropdown-menu-item_${child.variant}` : '',
                          toValue(child.disabled) && 'dropdown-menu-item_disabled',
                        ]"
                        :disabled="toValue(child.disabled)"
                      >
                        <component
                          :is="componentType(child)"
                          :href="child.href"
                          :to="child.to"
                          :data-test="`dropdown-menu-item-${child.key}`"
                          class="dropdown-menu-item"
                          :class="toValue(child.disabled) && 'dropdown-menu-item_disabled'"
                          @click="(e: Event) => { if (toValue(child.disabled)) e.preventDefault(); onItemClick(child); }"
                        >
                          <Icon v-if="child.icon" :icon="child.icon" class="p-0 -ml-[5px]" size="sm" />
                          {{ child.label }}
                          <slot name="item-trailing" :item="child" />
                        </component>
                      </DropdownMenuItem>
                    </template>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuItem
                v-else
                as-child
                :class="[
                  item.variant ? `dropdown-menu-item_${item.variant}` : '',
                  toValue(item.disabled) && 'dropdown-menu-item_disabled',
                ]"
                :disabled="toValue(item.disabled)"
              >
                <component
                  :is="componentType(item)"
                  :href="item.href"
                  :to="item.to"
                  :data-test="`dropdown-menu-item-${item.key}`"
                  class="dropdown-menu-item"
                  :class="toValue(item.disabled) && 'dropdown-menu-item_disabled'"
                  @click="(e: Event) => { if (toValue(item.disabled)) e.preventDefault(); onItemClick(item); }"
                >
                  <Icon v-if="item.icon" :icon="item.icon" class="p-0 -ml-[5px]" size="sm" />
                  {{ item.label }}
                  <slot name="item-trailing" :item="item" />
                </component>
              </DropdownMenuItem>
            </template>
          </template>
        </slot>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>

<style lang="postcss" scoped>
@reference "../../../assets/styles.css";

.dropdown-menu-item {
  display: flex;
  align-items: center;
  text-align: left;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  white-space: nowrap;
  transition: ease-in;
  width: 100%;
  border-radius: 0.75rem;
  cursor: pointer;
}

:deep(.dropdown-menu-item:hover),
:deep(.dropdown-menu-item:focus),
:deep(.dropdown-menu-item_highlighted) {
  background-color: #e9ecef;

  @apply text-primary;
}

:deep(.dropdown-menu-item_danger) {
  @apply text-danger;
}

:deep(.dropdown-menu-item_danger:hover),
:deep(.dropdown-menu-item_danger:focus) {
  @apply text-white bg-danger;
}

:deep(.dropdown-menu-item_disabled),
:deep(.dropdown-menu-item_disabled:hover),
:deep(.dropdown-menu-item_disabled:focus) {
  color: #9ca3af;
  background-color: transparent;
  opacity: 0.85;
  pointer-events: none;
  cursor: not-allowed;
}

:deep(.dropdown-menu-item_disabled.dropdown-menu-item_danger) {
  color: #d1d5db;
}
</style>
